import { z } from 'zod';
import type { AccessLevel } from './constants';

export type ClipStatus = 'uploading' | 'processing' | 'ready' | 'failed';

export interface TranscriptSegment {
  /** Seconds from the start. */
  start: number;
  end: number;
  text: string;
}

export interface ClipDTO {
  id: string;
  title: string;
  spaceId: string | null;
  resourceType: string | null;
  resourceId: string | null;
  mime: string;
  sizeBytes: number;
  durationMs: number;
  thumbnail: string | null;
  transcript: { segments: TranscriptSegment[] } | null;
  status: ClipStatus;
  publicToken: string | null;
  viewCount: number;
  /** How far the caller has watched, in ms. */
  myWatchedMs: number;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  access: AccessLevel;
  /** Same-origin streaming URL (signed-in). */
  url: string;
}

export interface PublicClipDTO {
  title: string;
  mime: string;
  durationMs: number;
  thumbnail: string | null;
  transcript: { segments: TranscriptSegment[] } | null;
  createdAt: number;
}

export interface ClipChangedEventData {
  clipId: string;
  op: 'created' | 'updated' | 'deleted' | 'ready';
}

/** R2 multipart parts must be at least this big, except the last one. */
export const CLIP_PART_BYTES = 5 * 1024 * 1024;
export const CLIP_MAX_MINUTES = 30;

const id = z.string().min(1).max(64);
const resourceType = z.enum(['task', 'doc', 'message', 'comment', 'office_floor']);

export const clipQuerySchema = z.object({
  spaceId: id.optional(),
  resourceType: resourceType.optional(),
  resourceId: id.optional(),
  q: z.string().max(200).optional(),
});

export const createClipSchema = z.object({
  title: z.string().trim().max(200).optional(),
  mime: z.string().max(80).default('video/webm'),
  spaceId: id.nullable().optional(),
  resourceType: resourceType.optional(),
  resourceId: id.optional(),
});
export type CreateClipInput = z.infer<typeof createClipSchema>;

export const completeClipSchema = z.object({
  parts: z.array(z.object({ partNumber: z.number().int().min(1).max(10_000), etag: z.string().max(200) })).min(1).max(10_000),
  durationMs: z.number().int().min(0).max(CLIP_MAX_MINUTES * 60_000 * 2),
  thumbnail: z.string().max(200_000).nullable().optional(),
  title: z.string().trim().max(200).optional(),
});
export type CompleteClipInput = z.infer<typeof completeClipSchema>;

export const updateClipSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  spaceId: id.nullable().optional(),
  /** Attach to (or detach from, with null) a task, doc, message or comment. */
  resourceType: resourceType.nullable().optional(),
  resourceId: id.nullable().optional(),
  isPublic: z.boolean().optional(),
});
export type UpdateClipInput = z.infer<typeof updateClipSchema>;

export const clipViewSchema = z.object({ watchedMs: z.number().int().min(0) });

/** "1:05" or "12:03:09" for a clip length or a transcript time. */
export function formatClipTime(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}
